import type { BalloonEventsQuery } from '../types/BalloonEventsQuery';
import type { LngLatGeolocation } from '../types/Geolocation';

/**
 * Partner embed configuration served by GET /api/v2/embed/config.
 * Keys align 1:1 with the webcomponent's option names; `style` is applied
 * to the host element instead of the options object.
 */
export interface EmbedServerConfig {
  language?: string;
  routingType?: 'hash' | 'browser';
  initialLocation?: LngLatGeolocation;
  initialZoom?: number;
  style?: string;
  /** Admin-authored corrective CSS, injected as a <style> tag next to the embed host element. */
  injectCss?: string;
  mapScrollable?: boolean;
  showDebug?: boolean;
  cooperativeGestures?: boolean;
  rootQuery?: BalloonEventsQuery;
  mobileRootQuery?: BalloonEventsQuery;
}

const BOOLEAN_KEYS = ['mapScrollable', 'showDebug', 'cooperativeGestures'] as const;
const QUERY_KEYS = ['rootQuery', 'mobileRootQuery'] as const;

/**
 * Revive ISO date strings inside a query's `dates[]` into Date objects.
 * Single source of truth for both root-query attribute parsing and the
 * server-config payload.
 */
export function reviveQueryDates<T extends { dates?: unknown }>(query: T): T {
  if (query && Array.isArray(query.dates)) {
    (query as { dates: Date[] }).dates = (query.dates as (string | number | Date)[]).map(
      (date) => new Date(date)
    );
  }
  return query;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== '';
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Reduce an untrusted /api/v2/embed/config payload to the known option keys
 * with the expected types. `null`/`undefined` values are dropped (so attribute
 * fallbacks survive a partially-configured backend) while `false` and `0`
 * are preserved; unknown keys never reach the options object.
 */
export function sanitizeEmbedServerConfig(json: unknown): EmbedServerConfig | null {
  if (!isPlainObject(json)) {
    return null;
  }

  const config: EmbedServerConfig = {};

  if (isNonEmptyString(json.language)) {
    config.language = json.language;
  }

  if (json.routingType === 'hash' || json.routingType === 'browser') {
    config.routingType = json.routingType;
  }

  if (isNonEmptyString(json.style)) {
    config.style = json.style;
  }

  if (isNonEmptyString(json.injectCss)) {
    config.injectCss = json.injectCss;
  }

  const location = json.initialLocation;
  if (isPlainObject(location) && isFiniteNumber(location.lng) && isFiniteNumber(location.lat)) {
    config.initialLocation = { lng: location.lng, lat: location.lat };
  }

  if (isFiniteNumber(json.initialZoom)) {
    config.initialZoom = json.initialZoom;
  }

  for (const key of BOOLEAN_KEYS) {
    const value = json[key];
    if (typeof value === 'boolean') {
      config[key] = value;
    }
  }

  for (const key of QUERY_KEYS) {
    const value = json[key];
    if (isPlainObject(value) && Object.keys(value).length > 0) {
      config[key] = reviveQueryDates({ ...value }) as BalloonEventsQuery;
    }
  }

  return config;
}
